/**
 * Visit: the one control that leaves the product for the person's own app.
 *
 * It exists as a component rather than as an anchor a screen writes itself for
 * three reasons, and all three are the kind of thing that goes wrong once per
 * screen otherwise: the new tab needs rel="noreferrer" every time, the arrow
 * has to be a drawn glyph rather than a character (an arrow that falls back to
 * a system face is the one wrong looking pixel on an otherwise careful page),
 * and a link that opens a new tab has to say so to a screen reader.
 *
 * It is never the ink block. Visiting your own app is the most reassuring
 * thing on the screen and almost never the next thing to do, so it renders as
 * a quiet label that gains contrast on hover. The one exception is the traffic
 * light's own action, which is a Button and belongs to that component.
 */

import { t } from '../../lib/i18n/t';
import '../../styles/components/visit-link.css';

export interface VisitLinkProps {
  /** The live web address, with its scheme. */
  href: string;
  /** Overrides the label. The arrow is always drawn. */
  label?: string;
  /** quiet sits at text-3 until hover, for a row. plain sits at text-2. */
  tone?: 'quiet' | 'plain';
}

export function VisitLink({ href, label, tone = 'plain' }: VisitLinkProps) {
  return (
    <a
      className="visit"
      data-tone={tone}
      href={href}
      target="_blank"
      rel="noreferrer noopener"
    >
      <span className="visit__label">{label ?? t('Visit')}</span>
      <span className="visit__arrow" aria-hidden="true">
        <svg viewBox="0 0 16 16" width="11" height="11" focusable="false">
          <path
            d="M5.5 10.5 10.5 5.5M6 5.5h4.5V10"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </span>
      <span className="visit__say">{t('opens in a new tab')}</span>
    </a>
  );
}
